import { useState, useEffect, useCallback, Fragment } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { Loader2, CheckCircle, AlertCircle, RefreshCw, Clock, ChevronDown, ChevronUp } from "lucide-react";
import { useToast } from "../../../contexts/ToastContext";

import { supabase } from "../../../lib/supabase";

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001';

function formatJakartaTime(dateString: string): string {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('id-ID', {
    timeZone: 'Asia/Jakarta',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).replace(/,/g, '');
}

export function PushTokenStatus() {
  const { showToast } = useToast();
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [limit] = useState(10);

  const fetchHistory = useCallback(async (page: number = 1) => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const offset = (page - 1) * limit;
      
      const response = await fetch(`${API_URL}/api/developer/push-token/history?limit=${limit}&offset=${offset}`, {
        headers: {
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch history');
      
      const result = await response.json();
      if (result.ok && result.data) {
        setHistory(result.data.logs ?? []);
        setTotalItems(result.data.total ?? 0);
        setTotalPages(Math.ceil((result.data.total ?? 0) / limit) || 1);
        setCurrentPage(page);
      }
    } catch (error: any) {
      console.error('Error fetching push token history:', error);
      showToast({ type: 'error', title: 'Error', message: 'Failed to load push token history' });
    } finally {
      setLoading(false);
    }
  }, [showToast, limit]);

  useEffect(() => {
    fetchHistory(1);
  }, [fetchHistory]);

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-500" />
              Stockbit Push Token History
            </CardTitle>
            <CardDescription>
              Execution history of the 5-minute push token update cron job ({totalItems} total)
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => fetchHistory(currentPage)} 
            disabled={loading}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-2 hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && (history?.length ?? 0) === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            <span>Loading history...</span>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Execution Time (WIB)</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(history?.length ?? 0) === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        No execution history found
                      </TableCell>
                    </TableRow>
                  ) : (
                    history.map((log) => (
                      <Fragment key={log.id}>
                        <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => toggleExpand(log.id)}>
                          <TableCell className="font-medium text-xs sm:text-sm">
                            {formatJakartaTime(log.started_at)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {log.status === 'completed' ? (
                                <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-green-200 text-[10px] sm:text-xs">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Success
                                </Badge>
                              ) : log.status === 'failed' ? (
                                <Badge variant="destructive" className="text-[10px] sm:text-xs">
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Failed
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="text-[10px] sm:text-xs">
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  {log.status}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-[200px] sm:max-w-[300px] truncate text-xs sm:text-sm">
                            {log.message || 'Successfully updated push token'}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); toggleExpand(log.id); }}>
                              {expandedId === log.id ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                              <span className="ml-1 hidden sm:inline">{expandedId === log.id ? 'Hide' : 'Details'}</span>
                            </Button>
                          </TableCell>
                        </TableRow>
                        {expandedId === log.id && (
                          <TableRow className="bg-muted/30">
                            <TableCell colSpan={4} className="p-4">
                              <div className="bg-slate-950 text-slate-50 p-4 rounded-md overflow-x-auto text-xs font-mono">
                                <div className="flex justify-between items-center mb-2 text-slate-400 border-b border-slate-800 pb-2">
                                  <span>Response Body</span>
                                  <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-800">JSON</Badge>
                                </div>
                                <pre className="whitespace-pre-wrap break-all">
                                  {JSON.stringify(log.response_data || { message: "No extra details available" }, null, 2)}
                                </pre>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination UI */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 px-2">
                <div className="text-xs text-muted-foreground text-center sm:text-left">
                  Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, totalItems)} of {totalItems} logs
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchHistory(currentPage - 1)}
                    disabled={currentPage === 1 || loading}
                    className="h-8 text-xs"
                  >
                    Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let startPage = Math.max(1, currentPage - 2);
                      const maxStartPage = Math.max(1, totalPages - 4);
                      startPage = Math.min(startPage, maxStartPage);
                      const page = startPage + i;

                      if (page > totalPages) return null;

                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => fetchHistory(page)}
                          disabled={loading}
                          className="w-8 h-8 p-0 text-xs"
                        >
                          {page}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchHistory(currentPage + 1)}
                    disabled={currentPage === totalPages || loading}
                    className="h-8 text-xs"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
